import React, { useEffect, useState } from 'react';
import { SaveIcon, BoldIcon, ItalicIcon } from 'lucide-react';
import { useNotificationBar } from '../../lib/cmsStore';
import { NotificationBarData } from '../../lib/cmsTypes';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { VersionHistoryPanel, VersionHistoryButton } from './components/VersionHistoryPanel';
export function NotificationEditor() {
  const {
    data,
    updateData,
    isLoading
  } = useNotificationBar();
  const [localData, setLocalData] = useState<NotificationBarData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  useEffect(() => {
    if (!isLoading && data && !localData) {
      setLocalData({
        ...data,
        backgroundColor: data.backgroundColor || '#C6FF00',
        textColor: data.textColor || '#000000',
        textBold: data.textBold ?? false,
        textItalic: data.textItalic ?? false
      });
    }
  }, [data, isLoading, localData]);
  if (isLoading || !localData) {
    return <div className="p-8 font-mono text-sm text-muted-foreground" data-id="element-1432">
        Loading...
      </div>;
  }
  const handleSave = () => {
    setIsSaving(true);
    updateData(localData);
    setTimeout(() => setIsSaving(false), 500);
  };
  const updateField = (field: keyof NotificationBarData, value: any) => {
    setLocalData(prev => prev ? {
      ...prev,
      [field]: value
    } : prev);
  };
  const previewTextStyle: React.CSSProperties = {
    fontWeight: localData.textBold ? 700 : 400,
    fontStyle: localData.textItalic ? 'italic' : 'normal'
  };
  return <div className="p-8 max-w-4xl mx-auto" data-id="element-1433">
      <div className="flex items-center justify-between mb-8" data-id="element-1434">
        <div data-id="element-1435">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1436">
            Notification Bar
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1437">
            Manage the global announcement bar displayed at the top of the
            website.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1438">
          <VersionHistoryButton onClick={() => setHistoryOpen(true)} data-id="element-1439" />
          <Button onClick={handleSave} disabled={isSaving} className="gap-2" data-id="element-1440">
            <SaveIcon className="w-4 h-4" data-id="element-1441" />
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      <div className="space-y-12 pb-24" data-id="element-1442">
        {/* Toggle */}
        <section className="space-y-6" data-id="element-1443">
          <div className="border-b border-border pb-2" data-id="element-1444">
            <h2 className="font-display text-2xl font-bold" data-id="element-1445">Status</h2>
          </div>
          <div className="flex items-center justify-between p-4 border border-border bg-card" data-id="element-1446">
            <div data-id="element-1447">
              <h3 className="font-mono text-sm font-bold uppercase tracking-widest" data-id="element-1448">
                Notification Bar
              </h3>
              <p className="font-mono text-xs text-muted-foreground mt-1" data-id="element-1449">
                {localData.enabled ? 'The notification bar is currently visible on the website.' : 'The notification bar is currently hidden.'}
              </p>
            </div>
            <button onClick={() => updateField('enabled', !localData.enabled)} className="shrink-0" aria-label="Toggle notification bar" data-id="element-1450">
              <span className={`inline-flex items-center w-12 h-7 rounded-full relative transition-colors duration-200 ${localData.enabled ? 'bg-primary' : 'bg-secondary'}`} data-id="element-1451">
                <span className={`w-5 h-5 rounded-full bg-background shadow-sm transition-transform duration-200 ${localData.enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1452" />
              </span>
            </button>
          </div>
        </section>

        {/* Content */}
        <section className="space-y-6" data-id="element-1453">
          <div className="border-b border-border pb-2" data-id="element-1454">
            <h2 className="font-display text-2xl font-bold" data-id="element-1455">Content</h2>
          </div>

          <div data-id="element-1456">
            <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2" data-id="element-1457">
              Message (supports HTML for links, etc.)
            </label>
            <textarea value={localData.message} onChange={e => updateField('message', e.target.value)} placeholder="e.g. No to war. Yes to peace, humanity, and dialogue." rows={2} className="w-full px-4 py-3 bg-secondary border border-border font-mono text-sm focus:outline-none focus:border-primary resize-none" data-id="element-1458" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1459">
            <Input label="Button Label (optional)" value={localData.buttonLabel} onChange={e => updateField('buttonLabel', e.target.value)} placeholder="e.g. Learn More" data-id="element-1460" />
            <Input label="Button Link (optional)" value={localData.buttonLink} onChange={e => updateField('buttonLink', e.target.value)} placeholder="e.g. https://example.com" data-id="element-1461" />
          </div>
        </section>

        {/* Appearance */}
        <section className="space-y-6" data-id="element-1462">
          <div className="border-b border-border pb-2" data-id="element-1463">
            <h2 className="font-display text-2xl font-bold" data-id="element-1464">Appearance</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1465">
            {/* Background Color */}
            <div data-id="element-1466">
              <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2" data-id="element-1467">
                Background Color
              </label>
              <div className="flex items-center gap-3" data-id="element-1468">
                <input type="color" value={localData.backgroundColor} onChange={e => updateField('backgroundColor', e.target.value)} className="w-10 h-10 border border-border cursor-pointer bg-transparent p-0.5" data-id="element-1469" />
                <input type="text" value={localData.backgroundColor} onChange={e => updateField('backgroundColor', e.target.value)} className="flex-1 px-4 py-2.5 bg-secondary border border-border font-mono text-sm focus:outline-none focus:border-primary" placeholder="#C6FF00" data-id="element-1470" />
              </div>
            </div>

            {/* Text Color */}
            <div data-id="element-1471">
              <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2" data-id="element-1472">
                Text Color
              </label>
              <div className="flex items-center gap-3" data-id="element-1473">
                <input type="color" value={localData.textColor} onChange={e => updateField('textColor', e.target.value)} className="w-10 h-10 border border-border cursor-pointer bg-transparent p-0.5" data-id="element-1474" />
                <input type="text" value={localData.textColor} onChange={e => updateField('textColor', e.target.value)} className="flex-1 px-4 py-2.5 bg-secondary border border-border font-mono text-sm focus:outline-none focus:border-primary" placeholder="#000000" data-id="element-1475" />
              </div>
            </div>
          </div>

          {/* Text Style */}
          <div data-id="element-1476">
            <label className="block font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2" data-id="element-1477">
              Text Style
            </label>
            <div className="flex items-center gap-2" data-id="element-1478">
              <button onClick={() => updateField('textBold', !localData.textBold)} className={`flex items-center justify-center w-10 h-10 border transition-colors ${localData.textBold ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-secondary text-muted-foreground hover:text-foreground'}`} aria-label="Toggle bold" title="Bold" data-id="element-1479">
                <BoldIcon className="w-4 h-4" data-id="element-1480" />
              </button>
              <button onClick={() => updateField('textItalic', !localData.textItalic)} className={`flex items-center justify-center w-10 h-10 border transition-colors ${localData.textItalic ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-secondary text-muted-foreground hover:text-foreground'}`} aria-label="Toggle italic" title="Italic" data-id="element-1481">
                <ItalicIcon className="w-4 h-4" data-id="element-1482" />
              </button>
            </div>
          </div>
        </section>

        {/* Preview */}
        <section className="space-y-6" data-id="element-1483">
          <div className="border-b border-border pb-2" data-id="element-1484">
            <h2 className="font-display text-2xl font-bold" data-id="element-1485">Preview</h2>
          </div>

          {localData.enabled && localData.message ? <div className="border border-border overflow-hidden" data-id="element-1486">
              <div className="w-full relative" style={{
            backgroundColor: localData.backgroundColor,
            color: localData.textColor
          }} data-id="element-1487">
                <div className="flex items-center justify-center gap-4 px-12 py-2.5 text-center" data-id="element-1488">
                  <span className="font-mono text-xs tracking-wide" style={previewTextStyle} dangerouslySetInnerHTML={{
                __html: localData.message
              }} data-id="element-1489" />
                  {localData.buttonLabel && <span className="inline-flex items-center font-mono text-xs font-bold uppercase tracking-widest px-3 py-1" style={{
                backgroundColor: localData.textColor,
                color: localData.backgroundColor
              }} data-id="element-1490">
                      {localData.buttonLabel}
                    </span>}
                </div>
              </div>
            </div> : <div className="p-8 border border-dashed border-border text-center" data-id="element-1491">
              <p className="font-mono text-sm text-muted-foreground" data-id="element-1492">
                {!localData.enabled ? 'Enable the notification bar and add a message to see a preview.' : 'Add a message to see a preview.'}
              </p>
            </div>}
        </section>
      </div>
      <VersionHistoryPanel contentKey="sinka_notification" isOpen={historyOpen} onClose={() => setHistoryOpen(false)} onRestore={data => {
      setLocalData(data);
      updateData(data);
    }} data-id="element-1493" />
    </div>;
}